import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import {
  requestPoolRelayQuote,
  type PoolRelayQuoteBody,
} from "../utils/relayer-client";
import type { StoredJoinSplitNote } from "../joinsplit/types";
import { candidateNotes } from "./noteCandidates";
import { selectForAmount } from "./spendSelection";

/** The relayer quotes for 60 s; a quote claiming over twice that is refused. */
export const POOL_RELAY_QUOTE_MAX_TTL_MS = 120_000;
/** Refresh a displayed quote this long before it expires, so review never
 * shows a quote about to lapse. */
export const POOL_RELAY_QUOTE_REFRESH_MARGIN_MS = 15_000;

/** A version-1 quote from POST /api/v1/relay/pool/quote (docs/EVM_POOL_RELAY.md
 * in stealthos-relayer). `fee` (what is charged) and `minimumFee` (what the
 * relayer's submission check accepts at the quoted cost) are in the withdrawn
 * token's base units. */
export interface PoolRelayQuote {
  version: 1;
  network: string;
  request: PoolRelayQuoteBody;
  feeRecipient: string;
  issuedAt: number;
  expiresAt: number;
  fee: string;
  feeNativeWei: string;
  minimumFee: string;
  minimumFeeNativeWei: string;
  networkFeeWei: string;
  l1DataFeeWei: string;
  allowanceWei: string;
}

export type PoolRelayQuoteShape = Omit<PoolRelayQuoteBody, "chainId" | "pool">;

/** Relayed withdrawals on this network are priced by a live quote. */
export function usesPoolRelayQuotes(): boolean {
  return (
    EVM_CONFIG.JOINSPLIT_V2.ENABLED &&
    EVM_CONFIG.JOINSPLIT_V2.RELAY_WITHDRAWALS &&
    EVM_CONFIG.JOINSPLIT_V2.RELAY_FEE_QUOTES === true
  );
}

export function poolRelayQuoteBody(
  shape: PoolRelayQuoteShape,
): PoolRelayQuoteBody {
  return {
    chainId: EVM_CONFIG.CHAIN_ID,
    pool: ethers.getAddress(EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT),
    kind: shape.kind,
    token: ethers.getAddress(shape.token),
    inputCount: shape.inputCount,
    outputCount: shape.outputCount,
    recipients: shape.recipients.map((r) => ethers.getAddress(r)),
  };
}

/** A stable key for "the same quote request". */
export function poolRelayQuoteKey(shape: PoolRelayQuoteShape): string {
  return JSON.stringify(poolRelayQuoteBody(shape));
}

/** Refuse anything but a quote for exactly this request. Its lifetime is
 * checked, never its timestamps against this device's clock, which may be
 * off the relayer's: callers time expiry from when they asked. */
export function assertPoolRelayQuote(
  quote: unknown,
  body: PoolRelayQuoteBody,
): PoolRelayQuote {
  const q = quote as Partial<PoolRelayQuote> | null;
  const invalid = (why: string): never => {
    throw new Error(`Invalid network fee quote: ${why}`);
  };
  if (!q || q.version !== 1) invalid("unsupported version");
  if (q!.network !== EVM_CONFIG.NETWORK?.key) invalid("network mismatch");
  const r = q!.request;
  const same = (a: string, b: string) => {
    try {
      return ethers.getAddress(a) === ethers.getAddress(b);
    } catch {
      return false;
    }
  };
  if (
    !r ||
    Number(r.chainId) !== body.chainId ||
    !same(r.pool, body.pool) ||
    r.kind !== body.kind ||
    !same(r.token, body.token) ||
    r.inputCount !== body.inputCount ||
    r.outputCount !== body.outputCount ||
    !Array.isArray(r.recipients) ||
    r.recipients.length !== body.recipients.length ||
    r.recipients.some((x, i) => !same(x, body.recipients[i]))
  )
    invalid("it does not match the request");
  const amount = (value: unknown) =>
    typeof value === "string" && /^[1-9][0-9]{0,40}$/.test(value);
  if (!amount(q!.fee)) invalid("invalid fee");
  if (!amount(q!.minimumFee) || BigInt(q!.minimumFee!) > BigInt(q!.fee!))
    invalid("invalid minimum fee");
  if (
    !Number.isSafeInteger(q!.issuedAt) ||
    !Number.isSafeInteger(q!.expiresAt) ||
    q!.expiresAt! <= q!.issuedAt! ||
    q!.expiresAt! - q!.issuedAt! > POOL_RELAY_QUOTE_MAX_TTL_MS
  )
    invalid("invalid lifetime");
  if (typeof q!.feeRecipient !== "string" || !ethers.isAddress(q!.feeRecipient))
    invalid("invalid fee recipient");
  return q as PoolRelayQuote;
}

export async function fetchPoolRelayQuote(
  shape: PoolRelayQuoteShape,
  options: { signal?: AbortSignal } = {},
): Promise<PoolRelayQuote> {
  const body = poolRelayQuoteBody(shape);
  const quote = await requestPoolRelayQuote(body, { signal: options.signal });
  return assertPoolRelayQuote(quote, body);
}

/** Whether a fee the user already approved still passes the relayer's
 * submission check for exactly this settlement at the live network cost. The
 * approved fee is bound as it is, never raised: the quoted fee exceeds
 * `minimumFee` by the relayer's allowance for cost movement, so a small rise
 * since the review needs no new one. */
export async function approvedRelayFeeCovers(
  shape: PoolRelayQuoteShape,
  approvedFee: bigint,
  options: { signal?: AbortSignal } = {},
): Promise<boolean> {
  const quote = await fetchPoolRelayQuote(shape, options);
  return approvedFee >= BigInt(quote.minimumFee);
}

/** The relayer fee bound into a proof on a quoted network: the configured
 * share of the amount, never below the live network cost quote. */
export function quotedRelayFee(
  amount: bigint,
  quote: Pick<PoolRelayQuote, "fee">,
  feeBps: number = EVM_CONFIG.JOINSPLIT_V2.RELAY_FEE_BPS,
): bigint {
  const share = (amount * BigInt(feeBps)) / 10_000n;
  const quoted = BigInt(quote.fee);
  return share > quoted ? share : quoted;
}

/** The inputs and change a relayed withdrawal of `amount` would use: the
 * same candidates and largest-first selection the settlement makes, on the
 * newest tree present. For pricing before proving; the settlement re-checks
 * with the notes it actually selects. Null when the balance cannot cover it. */
export function estimateWithdrawalShape(
  notes: StoredJoinSplitNote[],
  tokenAddress: string,
  amount: bigint,
): { inputCount: number; outputCount: 0 | 1 } | null {
  try {
    const candidates = candidateNotes(
      notes,
      ethers.getAddress(EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT),
      BigInt(EVM_CONFIG.CHAIN_ID),
      tokenAddress,
    );
    if (!candidates.length) return null;
    const tree = Math.max(...candidates.map((n) => n.note.treeIndex!));
    const selected = selectForAmount(
      candidates.filter((n) => n.note.treeIndex === tree),
      amount,
    );
    const total = selected.reduce((sum, n) => sum + n.note.value, 0n);
    return { inputCount: selected.length, outputCount: total > amount ? 1 : 0 };
  } catch {
    return null;
  }
}
